import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "components/ui/form";
import { Input } from "components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/ui/select";
import { toast } from "components/ui/use-toast";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useCreateNcmMutation } from "@/services/ncm";
import { useDialogModal } from "@/hook/handle-modal/hooks/actions";
import { ICreateNcm } from "@/@types/ncm";
import { UF_OPTIONS, formatTaxValue } from "./utils";
import { MESSAGES } from "./utils/messages";
import { FormSchema } from "./validation-forms";
import { Label } from "components/ui/label";
import { ContentContainer, PageLayout, PageTitle } from "components/layout";

export function CreateNcm() {
  const [createNcm, { isLoading }] = useCreateNcmMutation();
  const { handleModal } = useDialogModal();
  const navigate = useNavigate();

  const form = useForm<ICreateNcm>({
    resolver: zodResolver(FormSchema),
  });

  async function onSubmit(data: ICreateNcm) {
    try {
      await createNcm({
        ...data,
        iva: data.iva.replace(",", "."),
        internal_tax_rate: data.internal_tax_rate.replace(",", "."),
        interstate_tax: data.interstate_tax.replace(",", "."),
      }).unwrap();

      toast(MESSAGES.CREATE.SUCCESS);
      handleModal({ isOpen: false });
      form.reset();
      navigate("/fiscal/ncm");
    } catch (e) {
      toast(MESSAGES.CREATE.ERROR);
    }
  }

  return (
    <PageLayout>
      <PageTitle
        title="Cadastro de NCM"
        description="Preencha os campos abaixo para adicionar um novo NCM."
      />
      <ContentContainer>
        <div className="flex flex-col w-full">
          <div className="bg-white p-8 rounded-lg shadow-lg w-full max-w-3xl">
            <Form {...form}>
              <form
                onSubmit={form.handleSubmit(onSubmit)}
                className="grid grid-cols-2 gap-6"
              >
                <div className="col-span-2">
                  <FormField
                    control={form.control}
                    name="ncm"
                    render={({ field }) => (
                      <FormItem>
                        <Label className="text-gray-700 font-medium">NCM</Label>
                        <Input
                          {...field}
                          hasError={!!form.formState.errors.ncm}
                          name="ncm"
                          placeholder="Digite o NCM"
                        />
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={form.control}
                  name="state"
                  render={({ field }) => (
                    <FormItem>
                      <Label className="text-gray-700 font-medium">UF</Label>
                      <Select
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                      >
                        <FormControl>
                          <SelectTrigger
                            hasError={!!form.formState.errors.state}
                          >
                            <SelectValue placeholder="Selecione uma UF" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {UF_OPTIONS.map((uf) => (
                            <SelectItem key={uf} value={uf}>
                              {uf}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="is_active"
                  render={({ field }) => (
                    <FormItem>
                      <Label className="text-gray-700 font-medium">
                        Status
                      </Label>
                      <Select
                        onValueChange={(value) =>
                          field.onChange(value === "true")
                        }
                        value={
                          field.value !== undefined ? String(field.value) : ""
                        }
                      >
                        <FormControl>
                          <SelectTrigger
                            hasError={!!form.formState.errors.is_active}
                          >
                            <SelectValue placeholder="Selecione o status" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="true">Ativo</SelectItem>
                          <SelectItem value="false">Inativo</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="iva"
                  render={({ field }) => (
                    <FormItem>
                      <Label className="text-gray-700 font-medium">
                        IVA (%)
                      </Label>
                      <Input
                        {...field}
                        hasError={!!form.formState.errors.iva}
                        name="iva"
                        placeholder="IVA (%)"
                        onChange={(e) =>
                          field.onChange(formatTaxValue(e.target.value))
                        }
                      />
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="internal_tax_rate"
                  render={({ field }) => (
                    <FormItem>
                      <Label className="text-gray-700 font-medium">
                        Taxa de Imposto Interno (%)
                      </Label>
                      <Input
                        {...field}
                        hasError={!!form.formState.errors.internal_tax_rate}
                        name="internal_tax_rate"
                        placeholder="Taxa de imposto interno (%)"
                        onChange={(e) =>
                          field.onChange(formatTaxValue(e.target.value))
                        }
                      />
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="interstate_tax"
                  render={({ field }) => (
                    <FormItem>
                      <Label className="text-gray-700 font-medium">
                        Taxa Interestadual (%)
                      </Label>
                      <Input
                        {...field}
                        hasError={!!form.formState.errors.interstate_tax}
                        name="interstate_tax"
                        placeholder="Taxa interestadual (%)"
                        onChange={(e) =>
                          field.onChange(formatTaxValue(e.target.value))
                        }
                      />
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="col-span-2 flex justify-end gap-4">
                  <Button
                    type="button"
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-200 transition"
                    onClick={() => navigate(-1)}
                  >
                    Voltar
                  </Button>

                  <Button type="submit" variant="default" isLoading={isLoading}>
                    Salvar
                  </Button>
                </div>
              </form>
            </Form>
          </div>
        </div>
      </ContentContainer>
    </PageLayout>
  );
}
